import moment from "moment";
import { IActionItem } from "../../../redux/actionItems";

/**
 * External leads (Meta forms, chat, website, etc.) carry event details under
 * inconsistent keys — sometimes top-level, sometimes inside `form_filled`,
 * `extras` or `meta_json`, with keys like "number_of_guests:" or
 * "preferred_travel_dates:". This resolves each Event Details field by
 * checking those sources in priority order, first by known aliases and then
 * by a key pattern, so the event lead form gets pre-filled wherever possible.
 */

export interface IResolvedExtLeadEventFields {
    event_type: string;
    event_uuid: string;
    other_event_name: string | null;
    function_date: string | null;
    event_time: string;
    event_time_uuid: string;
    preferred_venue: string;
    preferred_venue_uuid: string;
    number_of_guests: number | null;
    number_of_rooms: number | null;
    estimated_rough_revenue: string | null;
    guest_budget: string | null;
}

type ISource = Record<string, any>;

// "Number of Guests:" -> "number_of_guests", "which_room_package?" -> "which_room_package"
const normalizeKey = (key: string) =>
    key
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

const parseObject = (value: any): ISource | null => {
    if (value && typeof value === "object" && !Array.isArray(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        try {
            const parsed = JSON.parse(value);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
                return parsed;
            }
        } catch { }
    }
    return null;
};

const normalizeSource = (source: ISource | null): ISource => {
    const result: ISource = {};
    if (!source) return result;
    Object.entries(source).forEach(([key, value]) => {
        // Meta lead forms sometimes send answers as single-item arrays
        const finalValue = Array.isArray(value) ? value[0] : value;
        const normalizedKey = normalizeKey(key);
        if (normalizedKey && !(normalizedKey in result)) {
            result[normalizedKey] = finalValue;
        }
    });
    return result;
};

const isPresent = (value: any) =>
    value !== null &&
    value !== undefined &&
    !(typeof value === "string" && value.trim() === "") &&
    typeof value !== "object";

// "65,000_plus_tax" -> 65000, "2" -> 2, "1.5 lakh" -> 150000, "50k" -> 50000
const parseNumber = (value: any): number | null => {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "string") return null;
    const match = value.match(/(\d[\d,]*(?:\.\d+)?)\s*(lakhs?|lacs?|k\b|thousand)?/i);
    if (!match) return null;
    let num = Number(match[1].replace(/,/g, ""));
    if (!Number.isFinite(num)) return null;
    const unit = (match[2] || "").toLowerCase();
    if (unit.startsWith("lakh") || unit.startsWith("lac")) num *= 100000;
    else if (unit === "k" || unit === "thousand") num *= 1000;
    return num;
};

const parsePositiveInt = (value: any): number | null => {
    const num = parseNumber(value);
    return num !== null && num > 0 ? Math.round(num) : null;
};

const parseAmount = (value: any): string | null => {
    const num = parseNumber(value);
    return num !== null && num > 0 ? String(num) : null;
};

const DATE_FORMATS = [
    moment.ISO_8601,
    "YYYY-MM-DD",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "D MMM YYYY",
    "D MMMM YYYY",
    "MMM D YYYY",
    "MMMM D YYYY",
    "D MMM, YYYY",
    "MMM D, YYYY",
];
const YEARLESS_DATE_FORMATS = ["D MMM", "D MMMM", "MMM D", "MMMM D", "DD-MM", "DD/MM"];

// Returns "YYYY-MM-DD" (the format CustomDatePicker emits). For ranges like
// "28 Nov - 1 Dec" the start date is used; year-less dates roll to the next
// occurrence so a past date isn't picked.
const parseDate = (value: any): string | null => {
    if (typeof value !== "string" || value.trim() === "") return null;
    const firstPart = value.split(/\s+(?:-|to|–)\s+/i)[0].replace(/(\d)(st|nd|rd|th)\b/gi, "$1").trim();

    const full = moment(firstPart, DATE_FORMATS as any, true);
    if (full.isValid()) return full.format("YYYY-MM-DD");

    const yearless = moment(firstPart, YEARLESS_DATE_FORMATS, true);
    if (yearless.isValid()) {
        if (yearless.isBefore(moment(), "day")) yearless.add(1, "year");
        return yearless.format("YYYY-MM-DD");
    }
    return null;
};

const parseText = (value: any): string | null =>
    isPresent(value) ? String(value).trim() : null;

interface IFieldRule<T> {
    aliases: string[];
    pattern?: RegExp;
    exclude?: RegExp;
    parse: (value: any) => T | null;
}

const resolveField = <T>(sources: ISource[], rule: IFieldRule<T>): T | null => {
    // Exact aliases win across all sources before falling back to patterns,
    // so a curated top-level value is never overridden by a fuzzy match.
    for (const source of sources) {
        for (const alias of rule.aliases) {
            if (!isPresent(source[alias])) continue;
            const parsed = rule.parse(source[alias]);
            if (parsed !== null) return parsed;
        }
    }
    if (!rule.pattern) return null;
    for (const source of sources) {
        for (const [key, value] of Object.entries(source)) {
            if (!rule.pattern.test(key)) continue;
            if (rule.exclude && rule.exclude.test(key)) continue;
            if (!isPresent(value)) continue;
            const parsed = rule.parse(value);
            if (parsed !== null) return parsed;
        }
    }
    return null;
};

export const resolveExtLeadEventFields = (
    actionItem: IActionItem
): IResolvedExtLeadEventFields => {
    const sources = [
        normalizeSource(actionItem as ISource),
        normalizeSource(parseObject(actionItem.form_filled)),
        normalizeSource(parseObject(actionItem.extras)),
        normalizeSource(parseObject(actionItem.meta_json)),
    ];

    const numberOfGuests =
        resolveField(sources, {
            aliases: [
                "number_of_guests",
                "no_of_guests",
                "num_of_guests",
                "guests",
                "guest_count",
                "total_guests",
                "pax",
                "no_of_pax",
                "number_of_pax",
                "headcount",
            ],
            pattern: /guest|pax|head_?count|people|persons?/,
            exclude: /name|budget|email|phone|mobile|contact|relation|type/,
            parse: parsePositiveInt,
        }) ??
        // Room-booking forms split guests into adults/children
        (() => {
            const adults = resolveField(sources, { aliases: ["adults", "no_of_adults", "number_of_adults"], parse: parsePositiveInt }) || 0;
            const children = resolveField(sources, { aliases: ["children", "kids", "no_of_children", "number_of_children"], parse: parsePositiveInt }) || 0;
            return adults + children > 0 ? adults + children : null;
        })();

    return {
        event_type:
            resolveField(sources, {
                aliases: ["event_type", "type_of_event", "event", "occasion", "function_type", "event_name"],
                pattern: /event_?type|type_of_(event|function)|occasion|function_type/,
                exclude: /uuid/,
                parse: parseText,
            }) || "",
        event_uuid: resolveField(sources, { aliases: ["event_uuid"], parse: parseText }) || "",
        other_event_name: resolveField(sources, { aliases: ["other_event_name"], parse: parseText }),
        function_date: resolveField(sources, {
            aliases: [
                "function_date",
                "event_date",
                "date_of_event",
                "date_of_function",
                "wedding_date",
                "check_in_date",
                "checkin_date",
                "preferred_date",
                "preferred_dates",
                "preferred_travel_dates",
                "travel_date",
                "travel_dates",
            ],
            pattern: /(event|function|wedding|party|check_?in|travel|preferred)_?dates?/,
            exclude: /followup|follow_up|created|insert|modified|check_?out/,
            parse: parseDate,
        }),
        event_time: resolveField(sources, {
            aliases: ["event_time", "event_timing", "function_time", "time_slot", "slot"],
            pattern: /(event|function)_?tim(e|ing)|time_?slot/,
            exclude: /uuid/,
            parse: parseText,
        }) || "",
        event_time_uuid: resolveField(sources, { aliases: ["event_time_uuid", "event_timing_uuid"], parse: parseText }) || "",
        preferred_venue: resolveField(sources, {
            aliases: ["preferred_venue", "venue", "venue_name", "preferred_venue_name"],
            pattern: /venue/,
            exclude: /uuid/,
            parse: parseText,
        }) || "",
        preferred_venue_uuid: resolveField(sources, { aliases: ["preferred_venue_uuid", "venue_uuid"], parse: parseText }) || "",
        number_of_guests: numberOfGuests,
        number_of_rooms: resolveField(sources, {
            aliases: ["number_of_rooms", "no_of_rooms", "num_of_rooms", "rooms", "room_count", "total_rooms"],
            pattern: /(no|number|num|count|total)_of_rooms?|rooms?_(count|required|needed)/,
            exclude: /category|type|package|preference/,
            parse: parsePositiveInt,
        }),
        estimated_rough_revenue: resolveField(sources, {
            aliases: ["estimated_rough_revenue", "estimated_revenue", "rough_revenue", "expected_revenue"],
            pattern: /revenue/,
            parse: parseAmount,
        }),
        guest_budget: resolveField(sources, {
            aliases: ["guest_budget", "budget", "quoted_rate_package"],
            // Package/price answers (e.g. "65,000_plus_tax_for_breakfast...") are the guest's budget
            pattern: /budget|package|price|quoted_rate/,
            exclude: /category|preference/,
            parse: parseAmount,
        }),
    };
};
